import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { Container, Row, Col, Form as BootstrapForm } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { BsArrowLeftCircleFill } from 'react-icons/bs';
import { addPatientAction } from "../../Action/modernAdminActions";
import { addPatientSchema } from '../../validations/patientSchema';
import { showLoading, dismissLoading, showSuccess, showError } from '../../utils/validationHelper';
import { parsePydanticValidationErrors } from '../../utils/errorHandler';
import config from '../../config/config';
import Dashboardheader from './dashboardheader';
import Sidebar from './sidebar';
import { Toaster } from 'react-hot-toast';
import Select from 'react-select';
import { LANGUAGE_OPTIONS } from '../../constants/languageOptions';
import { UAE_STATE_OPTIONS } from '../../constants/uaeStates';
import "../componentCss/webform.css";
import "../componentCss/sidebar.css";

// Common country codes for mobile numbers
const COUNTRY_CODES = [
  { code: "+971", country: "🇦🇪 UAE", flag: "🇦🇪" },
  { code: "+1", country: "🇺🇸 USA", flag: "🇺🇸" },
  { code: "+44", country: "🇬🇧 UK", flag: "🇬🇧" },
  { code: "+91", country: "🇮🇳 India", flag: "🇮🇳" },
  { code: "+92", country: "🇵🇰 Pakistan", flag: "🇵🇰" },
  { code: "+880", country: "🇧🇩 Bangladesh", flag: "🇧🇩" },
  { code: "+63", country: "🇵🇭 Philippines", flag: "🇵🇭" },
  { code: "+20", country: "🇪🇬 Egypt", flag: "🇪🇬" },
  { code: "+962", country: "🇯🇴 Jordan", flag: "🇯🇴" },
  { code: "+961", country: "🇱🇧 Lebanon", flag: "🇱🇧" },
  { code: "+966", country: "🇸🇦 Saudi Arabia", flag: "🇸🇦" },
  { code: "+968", country: "🇴🇲 Oman", flag: "🇴🇲" },
  { code: "+973", country: "🇧🇭 Bahrain", flag: "🇧🇭" },
  { code: "+974", country: "🇶🇦 Qatar", flag: "🇶🇦" },
  { code: "+965", country: "🇰🇼 Kuwait", flag: "🇰🇼" },
  { code: "+60", country: "🇲🇾 Malaysia", flag: "🇲🇾" },
  { code: "+65", country: "🇸🇬 Singapore", flag: "🇸🇬" },
  { code: "+66", country: "🇹🇭 Thailand", flag: "🇹🇭" },
  { code: "+84", country: "🇻🇳 Vietnam", flag: "🇻🇳" },
  { code: "+86", country: "🇨🇳 China", flag: "🇨🇳" },
  { code: "+81", country: "🇯🇵 Japan", flag: "🇯🇵" },
  { code: "+82", country: "🇰🇷 South Korea", flag: "🇰🇷" },
  { code: "+61", country: "🇦🇺 Australia", flag: "🇦🇺" },
  { code: "+64", country: "🇳🇿 New Zealand", flag: "🇳🇿" },
  { code: "+49", country: "🇩🇪 Germany", flag: "🇩🇪" },
  { code: "+33", country: "🇫🇷 France", flag: "🇫🇷" },
  { code: "+39", country: "🇮🇹 Italy", flag: "🇮🇹" },
  { code: "+34", country: "🇪🇸 Spain", flag: "🇪🇸" },
  { code: "+31", country: "🇳🇱 Netherlands", flag: "🇳🇱" },
  { code: "+41", country: "🇨🇭 Switzerland", flag: "🇨🇭" },
  { code: "+46", country: "🇸🇪 Sweden", flag: "🇸🇪" },
  { code: "+47", country: "🇳🇴 Norway", flag: "🇳🇴" },
  { code: "+45", country: "🇩🇰 Denmark", flag: "🇩🇰" },
  { code: "+7", country: "🇷🇺 Russia", flag: "🇷🇺" },
  { code: "+55", country: "🇧🇷 Brazil", flag: "🇧🇷" },
  { code: "+54", country: "🇦🇷 Argentina", flag: "🇦🇷" },
  { code: "+52", country: "🇲🇽 Mexico", flag: "🇲🇽" },
  { code: "+27", country: "🇿🇦 South Africa", flag: "🇿🇦" },
];

// Valid emergency contact relationships
const VALID_RELATIONSHIPS = [
  'spouse', 'parent', 'child', 'sibling', 'friend', 'colleague',
  'guardian', 'grandparent', 'grandchild', 'aunt', 'uncle', 'cousin',
  'other family', 'caregiver', 'other'
];

// Religion options with codes and labels
const RELIGION_OPTIONS = [
  { code: "BUD", label: "Buddhist" },
  { code: "BMA", label: "Buddhist: Mahayana" },
  { code: "BTH", label: "Buddhist: Theravada" },
  { code: "CHR", label: "Christian" },
  { code: "ANG", label: "Christian: Anglican" },
  { code: "BAP", label: "Christian: Baptist" },
  { code: "CHS", label: "Christian: Christian Science" },
  { code: "NAZ", label: "Christian: Church of the Nazarene" },
  { code: "EPI", label: "Christian: Episcopalian" },
  { code: "GRE", label: "Christian: Greek Orthodox" },
  { code: "JWN", label: "Christian: Jehovah's Witness" },
  { code: "LUT", label: "Christian: Lutheran" },
  { code: "MEN", label: "Christian: Mennonite" },
  { code: "MET", label: "Christian: Methodist" },
  { code: "PEN", label: "Christian: Pentecostal" },
  { code: "PRE", label: "Christian: Presbyterian" },
  { code: "PRO", label: "Christian: Protestant" },
  { code: "CAT", label: "Christian: Roman Catholic" },
  { code: "SEV", label: "Christian: Seventh Day Adventist" },
  { code: "UNU", label: "Christian: Unitarian Universalist" },
  { code: "ERL", label: "Ethnic Religionist" },
  { code: "HIN", label: "Hindu" },
  { code: "HOT", label: "Hindu: Other" },
  { code: "JAI", label: "Jain" },
  { code: "JEW", label: "Jewish" },
  { code: "MOS", label: "Muslim" },
  { code: "MOT", label: "Muslim: Other" },
  { code: "MSH", label: "Muslim: Shiite" },
  { code: "MSU", label: "Muslim: Sunni" },
  { code: "OTH", label: "Other" },
  { code: "SHN", label: "Shintoist" },
  { code: "SIK", label: "Sikh" },
  { code: "VAR", label: "Unknown" }
].sort((a, b) => a.label.localeCompare(b.label));

// Helper function to format Emirates ID with dashes: 784-1234-5678-901-2
const formatEmiratesId = (value) => {
  if (!value) return '';

  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');

  // Limit to 15 digits
  const limited = digitsOnly.slice(0, 15);

  // Format as: 784-XXXX-XXXX-XXX-X
  if (limited.length <= 3) return limited;
  if (limited.length <= 7) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  if (limited.length <= 11) return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
  if (limited.length <= 14) return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7, 11)}-${limited.slice(11)}`;
  return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7, 11)}-${limited.slice(11, 14)}-${limited.slice(14, 15)}`;
};

const AddPatientCredentials = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Add global error handler
  useEffect(() => {
    console.log('🔧 AddPatientCredentials component mounted');

    const handleGlobalError = (event) => {
      console.error('💥 Global error caught:', event.error);
      console.error('Error details:', {
        message: event.error?.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    };

    const handleUnhandledRejection = (event) => {
      console.error('💥 Unhandled promise rejection:', event.reason);
      console.error('Promise rejection details:', {
        reason: event.reason,
        promise: event.promise
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const initialValues = {
    // Personal Information - EMR Required
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    religion: "",
    race: "",
    language: "",
    marital_status: "",
    nationality: "",
    country: "UAE",
    state: "",
    citizenship: "",
    city: "",

    // Contact Information - EMR Required
    phone_country_code: "+971",
    phone_number: "",
    permanent_address: "",

    // Medical Information - EMR Required
    allergy: "",
    primary_physician: "",
    primary_physician_license: "",

    // Identification - EMR Required
    emirates_id: "",

    // Optional Fields
    passport_number: "",
    insurance_provider: "",
    insurance_policy_number: "",
    insurance_subscriber_name: "",

    // Emergency Contact - Optional
    emergency_contact_name: "",
    emergency_contact_country_code: "+971",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",

    // Patient Classification Flags
    is_vip: false,
    is_tourist: false
  };

  // Use the enhanced validation schema
  const validationSchema = addPatientSchema;

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError, setErrors }) => {
    console.log('🚀 SUBMIT BUTTON CLICKED - Starting pre-registration submission');
    console.log('📋 Form values received:', values);

    const loadingToast = showLoading('Creating patient...');
    setIsLoading(true);

    try {
      console.log('🔄 Transforming form values to API format...');

      // Transform form values to API format
      const patientData = {
        demographics: {
          // Personal Information - Required
          first_name: values.first_name,
          last_name: values.last_name,
          date_of_birth: values.date_of_birth ? new Date(values.date_of_birth).toISOString() : null,
          gender: values.gender,
          religion: values.religion,
          race: values.race,
          language: values.language,
          marital_status: values.marital_status,
          nationality: values.nationality,
          country: values.country,
          state: values.state,
          citizenship: values.citizenship,
          city: values.city,

          // Contact Information - Required
          phone_primary: values.phone_country_code + values.phone_number,
          permanent_address: values.permanent_address,

          // Medical Information - Required
          allergy: values.allergy,
          primary_physician: values.primary_physician,
          primary_physician_license: values.primary_physician_license,

          // Identification - Required
          emirates_id: values.emirates_id ? values.emirates_id.replace(/[-\s]/g, '') : values.emirates_id,

          // Optional Fields
          passport_number: values.passport_number || null,
          insurance_provider: values.insurance_provider || null,
          insurance_policy_number: values.insurance_policy_number || null,
          insurance_subscriber_name: values.insurance_subscriber_name || null,

          // Emergency Contact - Optional
          emergency_contact_name: values.emergency_contact_name || null,
          emergency_contact_phone: values.emergency_contact_phone ?
            values.emergency_contact_country_code + values.emergency_contact_phone : null,
          emergency_contact_relationship: values.emergency_contact_relationship || null,

          // Patient Classification Flags
          is_vip: values.is_vip || false,
          is_tourist: values.is_tourist || false
        },
        planned_services: []
      };

      console.log('📤 Prepared patient data for API:', patientData);
      console.log('🌐 About to call addPatientAction...');

      const response = await addPatientAction(patientData);

      console.log('✅ API call completed successfully');
      console.log('📥 API Response:', response);

      dismissLoading(loadingToast);

      if (response.success || response.id) {
        console.log('🎉 Patient creation successful, navigating...');
        showSuccess('Patient created successfully!');

        // Reset form for potential new entries
        resetForm();

        // Navigate with state to trigger refresh
        setTimeout(() => {
          console.log('🔄 Navigating to patient credentials page...');
          navigate(`${config.baseUrl}patient-records`, {
            state: {
              refresh: true,
              message: 'Patient created successfully',
              newPatientId: response.id
            }
          });
        }, 1500);
      } else {
        console.error('❌ API returned success=false or no ID');
        console.error('Response details:', response);
        showError(response.message || "Failed to create patient");
      }
    } catch (error) {
      console.error('💥 ERROR in handleSubmit:', error.message);

      dismissLoading(loadingToast);

      // Handle RBAC/Permission errors (403) explicitly
      const status = error?.response?.status;
      if (status === 403) {
        const permissionMessage = error?.response?.data?.detail ||
          'You do not have permission to create patients.';
        showError(permissionMessage);
        setIsLoading(false);
        setSubmitting(false);
        return;
      }

      // Parse Pydantic validation errors
      const { hasValidationErrors, fieldErrors, firstError } = parsePydanticValidationErrors(error);

      if (hasValidationErrors) {
        console.log('🔍 Parsed validation errors:', fieldErrors);

        // Set field errors in Formik
        setErrors(fieldErrors);

        // Show error toast
        showError(`Validation failed: ${firstError.message}`);

        // Auto-scroll to first error field
        if (firstError && firstError.field) {
          setTimeout(() => {
            const fieldElement = document.querySelector(`[name="${firstError.field}"]`);
            if (fieldElement) {
              fieldElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              fieldElement.focus();
            }
          }, 100);
        }
      } else {
        // Generic error handling for non-validation errors
        const errorMessage = error.response?.data?.error?.message ||
                            error.response?.data?.detail ||
                            error.message ||
                            "Failed to create patient";
        console.error('🚨 Showing error to user:', errorMessage);
        showError(errorMessage);
      }
    } finally {
      console.log('🏁 handleSubmit finally block - cleaning up');
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dashboardheader />
      <Toaster />
      <div className="wrapper">
        <Sidebar />
        <div id="content">
          <Container fluid className="p-4">
            <Row>
              <Col lg={12} md={12} sm={12} className="">
                <div className="innerhead mb-4">
                  <h5 className="headtitle">Add New Patient - EMR Registration</h5>
                </div>
                <Link to={`${config.baseUrl}patient-records`}>
                  <span style={{ float: "right" }}>
                    Back <BsArrowLeftCircleFill />
                  </span>
                </Link>
              </Col>
            </Row>
            <Row>
              <Col lg={12} md={12} sm={12} className="">
                <div className="formDiv">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validateOnMount={false}
                    enableReinitialize={false}
                  >
                    {({
                      errors,
                      handleSubmit,
                      handleBlur,
                      handleChange,
                      touched,
                      setFieldValue,
                      setFieldTouched,
                      values,
                      isValid,
                      dirty,
                      isSubmitting,
                      validateForm
                    }) => {
                      const handleFormSubmit = async (e) => {
                        e.preventDefault(); // Prevent default form submission
                        
                        console.log('🔘 FORM SUBMIT EVENT TRIGGERED');
                        console.log('📋 Current form values:', values);
                        console.log('✅ Form is valid:', isValid);
                        console.log('📝 Form is dirty:', dirty);
                        console.log('🚫 Form is submitting:', isSubmitting);
                        console.log('❌ Form errors:', errors);

                        // Validate form before submission
                        const validationErrors = await validateForm();
                        console.log('🔍 Validation result:', validationErrors);

                        if (Object.keys(validationErrors).length > 0) {
                          console.error('❌ Form validation failed:', validationErrors);
                          
                          // Force touch identification fields if they have validation errors
                          if (validationErrors.emirates_id || validationErrors.passport_number) {
                            setFieldTouched('emirates_id', true);
                            setFieldTouched('passport_number', true);
                          }
                          
                          return;
                        }

                        console.log('✅ Form validation passed, proceeding with submission...');
                        handleSubmit(e);
                      };

                      return (
                        <Form onSubmit={handleFormSubmit} autoComplete="off">
                          {/* Personal Information */}
                          <h6 className="mb-3">Personal Information</h6>
                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>First Name *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="first_name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.first_name}
                                placeholder="Enter first name"
                                isInvalid={touched.first_name && errors.first_name}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.first_name}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Last Name *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="last_name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.last_name}
                                placeholder="Enter last name"
                                isInvalid={touched.last_name && errors.last_name}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.last_name}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Religion *</BootstrapForm.Label>
                              <BootstrapForm.Select
                                name="religion"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.religion}
                                isInvalid={touched.religion && errors.religion}
                                required
                              >
                                <option value="">Select religion</option>
                                {RELIGION_OPTIONS.map((religion) => (
                                  <option key={religion.code} value={religion.code}>
                                    {religion.label}
                                  </option>
                                ))}
                              </BootstrapForm.Select>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.religion}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Date of Birth *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="date"
                                name="date_of_birth"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.date_of_birth}
                                isInvalid={touched.date_of_birth && errors.date_of_birth}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.date_of_birth}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Gender *</BootstrapForm.Label>
                              <BootstrapForm.Select
                                name="gender"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.gender}
                                isInvalid={touched.gender && errors.gender}
                                required
                              >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                              </BootstrapForm.Select>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.gender}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Race *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="race"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.race}
                                placeholder="Enter race (e.g., Asian, Caucasian, African)"
                                isInvalid={touched.race && errors.race}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.race}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Language *</BootstrapForm.Label>
                              <Select
                                name="language"
                                options={LANGUAGE_OPTIONS}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                onChange={(selectedOption) => setFieldValue("language", selectedOption?.value || "")}
                                onBlur={handleBlur}
                                value={LANGUAGE_OPTIONS.find(lang => lang.value === values.language) || null}
                                placeholder="Search and select language..."
                                isClearable={false}
                                isSearchable={true}
                                aria-required="true"
                                aria-invalid={touched.language && errors.language ? "true" : "false"}
                              />
                              {touched.language && errors.language && (
                                <div className="text-danger mt-1">
                                  {errors.language}
                                </div>
                              )}
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Marital Status *</BootstrapForm.Label>
                              <BootstrapForm.Select
                                name="marital_status"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.marital_status}
                                isInvalid={touched.marital_status && errors.marital_status}
                                required
                              >
                                <option value="">Select Marital Status</option>
                                <option value="single">Single</option>
                                <option value="married">Married</option>
                                <option value="divorced">Divorced</option>
                                <option value="widowed">Widowed</option>
                                <option value="separated">Separated</option>
                              </BootstrapForm.Select>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.marital_status}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Nationality *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="nationality"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.nationality}
                                placeholder="Enter nationality"
                                isInvalid={touched.nationality && errors.nationality}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.nationality}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Country of Residence *</BootstrapForm.Label>
                              <BootstrapForm.Select
                                name="country"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.country}
                                isInvalid={touched.country && errors.country}
                                required
                              >
                                <option value="UAE">United Arab Emirates</option>
                                <option value="USA">United States</option>
                                <option value="UK">United Kingdom</option>
                                <option value="Canada">Canada</option>
                                <option value="Australia">Australia</option>
                                <option value="Other">Other</option>
                              </BootstrapForm.Select>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.country}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>State *</BootstrapForm.Label>
                              <BootstrapForm.Select
                                name="state"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.state}
                                isInvalid={touched.state && errors.state}
                                required
                              >
                                <option value="">Select state or emirate</option>
                                {UAE_STATE_OPTIONS.map((state) => (
                                  <option key={state.value} value={state.value}>
                                    {state.label}
                                  </option>
                                ))}
                              </BootstrapForm.Select>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.state}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Citizenship *</BootstrapForm.Label>
                              <BootstrapForm.Select
                                name="citizenship"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.citizenship}
                                isInvalid={touched.citizenship && errors.citizenship}
                                required
                              >
                                <option value="">Select citizenship</option>
                                <option value="UAE">UAE Citizen</option>
                                <option value="Resident">UAE Resident</option>
                                <option value="Visitor">Visitor</option>
                              </BootstrapForm.Select>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.citizenship}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>City *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="city"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.city}
                                placeholder="Enter city"
                                isInvalid={touched.city && errors.city}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.city}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          {/* Contact Information */}
                          <h6 className="mb-3 mt-4">Contact Information</h6>
                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Label>Phone Number *</BootstrapForm.Label>
                              <div className="d-flex gap-2">
                                <BootstrapForm.Select
                                  name="phone_country_code"
                                  onChange={handleChange}
                                  value={values.phone_country_code}
                                  style={{ maxWidth: '140px', minWidth: '140px' }}
                                  required
                                >
                                  {COUNTRY_CODES.map((country) => (
                                    <option key={country.code} value={country.code}>
                                      {country.flag} {country.code}
                                    </option>
                                  ))}
                                </BootstrapForm.Select>
                                <BootstrapForm.Control
                                  type="tel"
                                  name="phone_number"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.phone_number}
                                  placeholder="501234567"
                                  isInvalid={touched.phone_number && errors.phone_number}
                                  required
                                  style={{ flex: 1 }}
                                />
                              </div>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.phone_number}
                              </BootstrapForm.Control.Feedback>
                              <BootstrapForm.Text className="text-muted">
                                Select country code and enter phone number without country code
                              </BootstrapForm.Text>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Label>Permanent Address *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                as="textarea"
                                rows={2}
                                name="permanent_address"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.permanent_address}
                                placeholder="Enter complete permanent address"
                                isInvalid={touched.permanent_address && errors.permanent_address}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.permanent_address}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          {/* Medical Information */}
                          <h6 className="mb-3 mt-4">Medical Information</h6>
                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={12}>
                              <BootstrapForm.Label>Allergy *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                as="textarea"
                                rows={2}
                                name="allergy"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.allergy}
                                placeholder="Known allergies (enter 'None' if no known allergies)"
                                isInvalid={touched.allergy && errors.allergy}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.allergy}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Label>Primary Physician *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="primary_physician"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.primary_physician}
                                placeholder="Name of primary physician (letters, spaces, apostrophes, hyphens, dots only)"
                                isInvalid={touched.primary_physician && errors.primary_physician}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.primary_physician}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Label>Primary Physician License Number *</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="primary_physician_license"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.primary_physician_license}
                                placeholder="License number of primary physician"
                                isInvalid={touched.primary_physician_license && errors.primary_physician_license}
                                required
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.primary_physician_license}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          {/* Emergency Contact */}
                          <h6 className="mb-3 mt-4">Emergency Contact</h6>
                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Emergency Contact Name</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="emergency_contact_name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.emergency_contact_name}
                                placeholder="Enter emergency contact name"
                                isInvalid={touched.emergency_contact_name && errors.emergency_contact_name}
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.emergency_contact_name}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Emergency Contact Phone</BootstrapForm.Label>
                              <div className="d-flex gap-2">
                                <BootstrapForm.Select
                                  name="emergency_contact_country_code"
                                  onChange={handleChange}
                                  value={values.emergency_contact_country_code}
                                  style={{ maxWidth: '120px', minWidth: '120px' }}
                                >
                                  {COUNTRY_CODES.map((country) => (
                                    <option key={country.code} value={country.code}>
                                      {country.flag} {country.code}
                                    </option>
                                  ))}
                                </BootstrapForm.Select>
                                <BootstrapForm.Control
                                  type="tel"
                                  name="emergency_contact_phone"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.emergency_contact_phone}
                                  placeholder="501234567"
                                  isInvalid={touched.emergency_contact_phone && errors.emergency_contact_phone}
                                  style={{ flex: 1 }}
                                />
                              </div>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.emergency_contact_phone}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Relationship</BootstrapForm.Label>
                              <BootstrapForm.Select
                                name="emergency_contact_relationship"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.emergency_contact_relationship}
                                isInvalid={touched.emergency_contact_relationship && errors.emergency_contact_relationship}
                              >
                                <option value="">Select relationship</option>
                                {VALID_RELATIONSHIPS.map((relationship) => (
                                  <option key={relationship} value={relationship}>
                                    {relationship.charAt(0).toUpperCase() + relationship.slice(1)}
                                  </option>
                                ))}
                              </BootstrapForm.Select>
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.emergency_contact_relationship}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          {/* Identification */}
                          <h6 className="mb-3 mt-4">Identification</h6>
                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Label>Emirates ID</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="emirates_id"
                                onChange={(e) => {
                                  const formatted = formatEmiratesId(e.target.value);
                                  setFieldValue('emirates_id', formatted);
                                }}
                                onBlur={handleBlur}
                                value={values.emirates_id}
                                placeholder="Enter Emirates ID (784-xxxx-xxxx-xxx-x)"
                                isInvalid={touched.emirates_id && errors.emirates_id}
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.emirates_id}
                              </BootstrapForm.Control.Feedback>
                              <BootstrapForm.Text className="text-muted">
                                Provide either Emirates ID or Passport Number
                              </BootstrapForm.Text>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Label>Passport Number</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="passport_number"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.passport_number}
                                placeholder="Enter passport number (AB1234567)"
                                isInvalid={touched.passport_number && errors.passport_number}
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.passport_number}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          {/* Insurance Information */}
                          <h6 className="mb-3 mt-4">Insurance Information</h6>
                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Insurance Provider</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="insurance_provider"
                                onChange={handleChange}
                                value={values.insurance_provider}
                                placeholder="Insurance provider name (optional)"
                              />
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Policy Number</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="insurance_policy_number"
                                onChange={handleChange}
                                value={values.insurance_policy_number}
                                placeholder="Policy number (optional)"
                              />
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={4}>
                              <BootstrapForm.Label>Subscriber Name</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="text"
                                name="insurance_subscriber_name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.insurance_subscriber_name}
                                placeholder="Subscriber name (optional)"
                                isInvalid={touched.insurance_subscriber_name && errors.insurance_subscriber_name}
                              />
                              <BootstrapForm.Control.Feedback type="invalid">
                                {errors.insurance_subscriber_name}
                              </BootstrapForm.Control.Feedback>
                            </BootstrapForm.Group>
                          </Row>

                          {/* Patient Classification */}
                          <h6 className="mb-3 mt-4">Patient Classification</h6>
                          <Row className="mb-3">
                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Check
                                type="checkbox"
                                name="is_vip"
                                label="VIP Patient"
                                checked={values.is_vip}
                                onChange={handleChange}
                              />
                              <BootstrapForm.Text className="text-muted">
                                Mark this patient as VIP for priority treatment
                              </BootstrapForm.Text>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group as={Col} md={6}>
                              <BootstrapForm.Check
                                type="checkbox"
                                name="is_tourist"
                                label="Tourist/Visitor"
                                checked={values.is_tourist}
                                onChange={handleChange}
                              />
                              <BootstrapForm.Text className="text-muted">
                                Mark this patient as a tourist or temporary visitor
                              </BootstrapForm.Text>
                            </BootstrapForm.Group>
                          </Row>

                          {/* Submit Button */}
                          <Row className="mb-4 mt-4">
                            <BootstrapForm.Group as={Col}>
                              {isLoading ? (
                                <button
                                  disabled
                                  className="btn btn-primary"
                                >
                                  Creating Patient...{" "}
                                  <img
                                    height="20"
                                    width="20px"
                                    src="assets/images/loading.gif"
                                    alt="Loading"
                                  />
                                </button>
                              ) : (
                                <button
                                  type="submit"
                                  className="btn btn-primary"
                                  style={{
                                    position: 'relative',
                                    zIndex: 1000,
                                    pointerEvents: 'auto',
                                    cursor: 'pointer',
                                    opacity: 1,
                                    display: 'inline-block'
                                  }}
                                  data-testid="create-patient-submit"
                                >
                                  Create Patient
                                </button>
                              )}
                            </BootstrapForm.Group>
                          </Row>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  );
};

export default AddPatientCredentials;
